"use client";

import "./index.css";
import { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import useAllLeagues from "@/hooks/useAllLeagues";
import AllLeaguesIcon from "@/assets/images/leagues/ic-all.webp";
import arrowDownIcon from "@/assets/images/kbv-icon-arrow-down.svg";

export default function LeagueFilterV25({
  selectedIds = [],
  onChange,
  className = "",
  scopeClassName = "",
  themeStyle,
}) {
  const { leagues, loading } = useAllLeagues();
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState(selectedIds || []);
  const [viewMode, setViewMode] = useState("league");
  const triggerRef = useRef(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState({});

  useEffect(() => {
    setTempSelectedIds(selectedIds || []);
  }, [selectedIds]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      const inTrigger = wrapperRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updateDropdownPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [open]);

  const getPortalTarget = () => {
    if (typeof document === "undefined") return null;
    return document.body;
  };

  const getPortalTheme = () =>
    wrapperRef.current?.closest(".keobongvip-root")?.getAttribute("data-theme") ??
    undefined;

  const updateDropdownPosition = () => {
    if (!triggerRef.current) return;
    const triggerRect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const padding = 8;
    const isMobile = window.innerWidth < 768;
    setDropdownStyle({
      position: "fixed",
      top: triggerRect.bottom + gap,
      ...(isMobile
        ? { left: padding, right: padding, width: window.innerWidth - padding * 2 }
        : { right: window.innerWidth - triggerRect.right, width: 550 }),
      zIndex: 99999,
    });
  };

  useLayoutEffect(() => {
    if (open && triggerRef.current) updateDropdownPosition();
  }, [open]);

  const normalizedLeagues = useMemo(
    () =>
      (leagues || []).map((l) => ({
        ...l,
        country: l.country || l.countryName || "Khác",
      })),
    [leagues],
  );

  const filteredLeagues = useMemo(() => {
    if (!keyword.trim()) return normalizedLeagues || [];
    const lower = keyword.toLowerCase();
    return (normalizedLeagues || []).filter((league) => {
      const nameVN = (league.nameVN || "").toLowerCase();
      const name = (league.name || "").toLowerCase();
      const country = (league.country || "").toLowerCase();
      return (
        nameVN.includes(lower) || name.includes(lower) || country.includes(lower)
      );
    });
  }, [normalizedLeagues, keyword]);

  const leaguesByCountry = useMemo(() => {
    const groups = {};
    for (const league of filteredLeagues) {
      const countryKey = league.country || "Khác";
      if (!groups[countryKey]) {
        groups[countryKey] = {
          country: countryKey,
          countryLogo: league.countryLogo || null,
          items: [],
        };
      }
      groups[countryKey].items.push(league);
    }
    return Object.values(groups);
  }, [filteredLeagues]);

  const handleToggleLeague = (id) => {
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = (filteredLeagues || []).map((l) => l.id);
    setTempSelectedIds(allIds);
  };

  const handleClearAll = () => {
    setTempSelectedIds([]);
  };

  const handleConfirm = () => {
    onChange?.(tempSelectedIds);
    setOpen(false);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;
  const triggerLabel = hasSelection
    ? `${selectedIds.length} giải đấu`
    : "Tất cả";

  return (
    <div ref={wrapperRef} className={`relative inline-block overflow-visible ${scopeClassName} ${className}`}>
      <button
        ref={triggerRef}
        className={`kbv-filter-v25-trigger flex items-center justify-center gap-2 rounded-lg border text-sm font-semi-bold md:px-3 px-2 h-9 flex items-center justify-between gap-2 md:!px-2 !px-1.5 md:min-w-[200px] w-full ${open ? "kbv-wr-dropdown-focus" : ""}`}
        type="button"
        onClick={() => {
          setOpen((prev) => {
            if (prev) return false;
            updateDropdownPosition();
            return true;
          });
        }}
      >
        <div
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          <div
            className="overflow-hidden bg-transparent cursor-pointer w-6 aspect-square flex-shrink-0 dropdown-icon"
          >
            <img
              data-v-d41374be=""
              className="set-img w-full h-full object-cover"
              id="dropdown-box-header-all-image"
              data-cy="dropdown-box-header-all-image"
              src={AllLeaguesIcon}
              alt=""
              loading="lazy"
            />
          </div>
          <p
            className="md:text-sm font-semi-bold text-xs font-semi-bold truncate text-[var(--theme-text)] m-0"
          >
            {triggerLabel}
          </p>
        </div>
        <img
          src={arrowDownIcon}
          alt="arrow down"
          className={`kbv-filter-v25-trigger-arrow w-5 h-5 flex-shrink-0 transition-[transform] duration-300 ease-in-out ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open &&
        getPortalTarget() &&
        createPortal(
          <div className="keobongvip-root" data-theme={getPortalTheme()} style={themeStyle}>
            <div
              ref={dropdownRef}
              style={dropdownStyle}
              className={`kbv-filter-v25-dropdown ${scopeClassName} flex max-h-[60vh] flex-col rounded-xl text-[var(--theme-text)]`}
            >
            <div className="flex shrink-0 items-center justify-between px-3 pb-1.5 pt-2">
              <div className="relative min-w-0 flex-1">
                <input
                  type="text"
                  placeholder="Tìm tên giải đấu"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  className="kbv-filter-v25-input w-full rounded-lg border py-2 pl-2.5 pr-9 text-sm outline-none"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--theme-text-muted)]">
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="kbv-filter-v25-close -m-2 ml-2 rounded p-2"
              >
                ✕
              </button>
            </div>

            <div className="kbv-filter-v25-view-bar shrink-0 border-b px-3 pb-2">
              <div className="flex items-center justify-between text-xs uppercase">
                <span className="font-medium text-[var(--theme-text-muted)]">Xem theo</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("league")}
                    className={`kbv-filter-v25-view-btn rounded px-3 py-1.5 text-xs font-medium ${
                      viewMode === "league" ? "kbv-filter-v25-view-btn--active" : ""
                    }`}
                  >
                    Giải đấu
                  </button>
                  <button
                    type="button"
                    onClick={() => setViewMode("country")}
                    className={`kbv-filter-v25-view-btn rounded px-3 py-1.5 text-xs font-medium ${
                      viewMode === "country" ? "kbv-filter-v25-view-btn--active" : ""
                    }`}
                  >
                    Quốc gia
                  </button>
                </div>
              </div>
            </div>

            <div className="kbv-filter-v25-list min-h-0 flex-1 overflow-auto py-2 pl-1 pr-2">
              {loading ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Đang tải danh sách giải đấu...
                </div>
              ) : filteredLeagues.length === 0 ? (
                <div className="py-8 text-center text-sm text-[var(--theme-text-muted)]">
                  Không tìm thấy giải đấu nào
                </div>
              ) : viewMode === "league" ? (
                <div className="grid grid-cols-2 gap-1.5">
                  {filteredLeagues.map((league) => {
                    const checked = tempSelectedIds.includes(league.id);
                    const displayName = league.nameVN || league.name || "";
                    return (
                      <button
                        key={league.id}
                        type="button"
                        onClick={() => handleToggleLeague(league.id)}
                        className="kbv-filter-v25-league-item flex min-w-0 items-center gap-1.5 rounded border py-1.5 pl-0.5 pr-1.5 text-left text-xs transition-colors"
                      >
                        <span
                          className={`kbv-filter-v25-check flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[9px] font-bold ${
                            checked ? "kbv-filter-v25-check--checked" : ""
                          }`}
                        >
                          {checked ? "✓" : ""}
                        </span>
                        {league.logo && (
                          <img
                            src={league.logo}
                            alt=""
                            className="h-5 w-5 shrink-0 object-contain"
                          />
                        )}
                        <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                          {displayName}
                        </span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  {leaguesByCountry.map((group) => (
                    <div key={group.country}>
                      <div className="flex items-center gap-1.5 px-1 py-0.5 text-[11px] font-semibold uppercase text-[var(--theme-text-muted)]">
                        {group.countryLogo && (
                          <img
                            src={group.countryLogo}
                            alt={group.country}
                            className="h-5 w-5 object-contain"
                          />
                        )}
                        <span>{group.country}</span>
                      </div>
                      <div className="mt-1 grid grid-cols-2 gap-1.5">
                        {group.items.map((league) => {
                          const checked = tempSelectedIds.includes(league.id);
                          const displayName = league.nameVN || league.name || "";
                          return (
                            <button
                              key={league.id}
                              type="button"
                              onClick={() => handleToggleLeague(league.id)}
                              className="kbv-filter-v25-league-item flex min-w-0 items-center gap-1.5 rounded border py-1.5 pl-0.5 pr-1.5 text-left text-xs transition-colors"
                            >
                              <span
                                className={`kbv-filter-v25-check flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[9px] font-bold ${
                                  checked ? "kbv-filter-v25-check--checked" : ""
                                }`}
                              >
                                {checked ? "✓" : ""}
                              </span>
                              {league.logo && (
                                <img
                                  src={league.logo}
                                  alt=""
                                  className="h-5 w-5 shrink-0 object-contain"
                                />
                              )}
                              <span className="min-w-0 truncate text-left" title={league.nameVN || league.name}>
                                {displayName}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="kbv-filter-v25-footer flex shrink-0 items-center justify-between gap-2 border-t px-3 py-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSelectAll}
                  className="kbv-filter-v25-action flex items-center gap-1.5 text-xs text-[var(--theme-text)]"
                >
                  <span
                    className={`kbv-filter-v25-check flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[10px] font-bold ${
                      filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id))
                        ? "kbv-filter-v25-check--checked"
                        : ""
                    }`}
                  >
                    {filteredLeagues.length > 0 && filteredLeagues.every((l) => tempSelectedIds.includes(l.id)) ? "✓" : ""}
                  </span>
                  Chọn tất cả
                </button>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="kbv-filter-v25-action flex items-center gap-1.5 text-xs text-[var(--theme-text)]"
                >
                  <span
                    className={`kbv-filter-v25-check flex h-4 w-4 shrink-0 items-center justify-center rounded border border-solid text-[10px] font-bold ${
                      tempSelectedIds.length === 0 ? "kbv-filter-v25-check--checked" : ""
                    }`}
                  >
                    {tempSelectedIds.length === 0 ? "✓" : ""}
                  </span>
                  Bỏ chọn tất cả
                </button>
              </div>
              <button
                type="button"
                onClick={handleConfirm}
                className="kbv-filter-v25-confirm shrink-0 rounded-lg px-4 py-1.5 text-xs font-semibold"
              >
                Xác nhận
              </button>
            </div>
          </div>
          </div>,
          getPortalTarget()
        )}
    </div>
  );
}
